import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Building2, Plus, Trash2, Edit3, X, Save, Eye,
  Image, Video, Users, Award, FileCheck,
  Sparkles, Target, Briefcase, HeartHandshake,
  Flag, Package, ExternalLink, Palette, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  Button, MessagePlugin, Input, Switch, Checkbox, Textarea, Tabs
} from 'tdesign-react';
import ProductImageUploader from '../../components/ProductImageUploader';
import ProductRichTextEditor from '../../components/ProductRichTextEditor';
import { DynamicIcon, IconPicker, ColorPickerField } from './components/CommonComponents';
import { ImagePreviewProvider, PreviewableImage } from './components/ImagePreview';
import { enterpriseSiteApi, tokenStorage } from '../../api';

// ============ 类型定义 ============
interface ImageTextItem {
  id: string;
  image: string;
  title: string;
  description: string;
}

interface IconTextItem {
  id: string;
  icon: string;
  iconBgColor: string;
  title: string;
  description: string;
}

interface ConfigData {
  id: number | null;
  companyName: string;
  companyUrl: string;
  companyImages: string[];
  companyDesc: string;
  selectedSections: string[];
  cultureShow: ImageTextItem[];
  honors: ImageTextItem[];
  leadershipCare: ImageTextItem[];
  qualifications: ImageTextItem[];
  investmentProjects: IconTextItem[];
  companyStrength: IconTextItem[];
  cooperation: ImageTextItem[];
  partyBuildingNews: ImageTextItem[];
  productIntro: ImageTextItem[];
  promoVideo: string;
  updatedAt: string;
}

// ============ 自定义选择配置选项 ============
const sectionOptions = [
  { value: 'cultureShow', label: '文化展示', icon: Sparkles },
  { value: 'honors', label: '荣誉资质', icon: Award },
  { value: 'leadershipCare', label: '领导关怀', icon: Users },
  { value: 'qualifications', label: '经营资质', icon: FileCheck },
  { value: 'investmentProjects', label: '投资项目', icon: Target },
  { value: 'companyStrength', label: '企业实力', icon: Building2 },
  { value: 'cooperation', label: '合作情况', icon: HeartHandshake },
  { value: 'partyBuildingNews', label: '党建动态', icon: Flag },
  { value: 'productIntro', label: '产品介绍', icon: Package },
  { value: 'promoVideo', label: '宣传视频', icon: Video },
];

// ============ 工具函数 ============
const genId = () => Date.now().toString() + Math.random().toString(36).slice(2, 6);

const emptyImageTextItem = (): ImageTextItem => ({
  id: genId(), image: '', title: '', description: ''
});

const emptyIconTextItem = (): IconTextItem => ({
  id: genId(), icon: 'star', iconBgColor: '#1c5bff', title: '', description: ''
});

const emptyConfig = (): ConfigData => ({
  id: null,
  companyName: '',
  companyUrl: '',
  companyImages: [],
  companyDesc: '',
  selectedSections: [],
  cultureShow: [],
  honors: [],
  leadershipCare: [],
  qualifications: [],
  investmentProjects: [],
  companyStrength: [],
  cooperation: [],
  partyBuildingNews: [],
  productIntro: [],
  promoVideo: '',
  updatedAt: ''
});

// URL 正则校验
const URL_REGEX = /^https?:\/\/([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/;
const isValidUrl = (url: string) => !url || URL_REGEX.test(url);

// ============ API ↔ ConfigData 转换 ============
const fromApiImageTextItem = (item: any): ImageTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  image: item?.image || '',
  title: item?.title || '',
  description: item?.description || '',
});

const fromApiIconTextItem = (item: any): IconTextItem => ({
  id: item?.id ? String(item.id) : genId(),
  icon: item?.icon || 'star',
  iconBgColor: item?.bgColor || '#1c5bff',
  title: item?.title || '',
  description: item?.description || '',
});

const toApiImageTextItem = (item: ImageTextItem) => ({
  image: item.image,
  title: item.title,
  description: item.description,
});

const toApiIconTextItem = (item: IconTextItem) => ({
  icon: item.icon,
  bgColor: item.iconBgColor,
  title: item.title,
  description: item.description,
});

const mapApiToConfig = (apiData: any): ConfigData => {
  if (!apiData) return emptyConfig();

  const customConfig = apiData.customDisplayConfig || {};

  return {
    id: apiData.id ?? null,
    companyName: apiData.enterpriseName || '',
    companyUrl: apiData.siteUrl || '',
    companyImages: apiData.enterpriseImage ? apiData.enterpriseImage.split(',').filter(Boolean) : [],
    companyDesc: apiData.introduction || '',
    selectedSections: Array.isArray(customConfig)
      ? customConfig
      : (customConfig.selectedSections || []),
    cultureShow: (apiData.cultureDisplay || []).map(fromApiImageTextItem),
    honors: (apiData.honors || []).map(fromApiImageTextItem),
    leadershipCare: (apiData.leadershipCare || []).map(fromApiImageTextItem),
    qualifications: (apiData.qualifications || []).map(fromApiImageTextItem),
    investmentProjects: (apiData.investmentProjects || []).map(fromApiIconTextItem),
    companyStrength: (apiData.enterpriseStrength || []).map(fromApiIconTextItem),
    cooperation: (apiData.cooperation || []).map(fromApiImageTextItem),
    partyBuildingNews: (apiData.partyBuilding || []).map(fromApiImageTextItem),
    productIntro: (apiData.productIntros || []).map(fromApiImageTextItem),
    promoVideo: apiData.promoVideo || '',
    updatedAt: apiData.updateTime || '',
  };
};

const mapConfigToApi = (config: ConfigData): any => {
  const tenantInfo = tokenStorage.getTenantInfo();

  const payload: any = {
    id: config.id ?? undefined,
    tenantId: tenantInfo?.tenantId ?? tenantInfo?.id,
    siteUrl: config.companyUrl,
    introduction: config.companyDesc,
    promoVideo: config.promoVideo,
    enterpriseName: config.companyName,
    enterpriseImage: config.companyImages.join(','),
    customDisplayConfig: config.selectedSections,
    cultureDisplay: config.cultureShow.map(toApiImageTextItem),
    honors: config.honors.map(toApiImageTextItem),
    leadershipCare: config.leadershipCare.map(toApiImageTextItem),
    qualifications: config.qualifications.map(toApiImageTextItem),
    investmentProjects: config.investmentProjects.map(toApiIconTextItem),
    enterpriseStrength: config.companyStrength.map(toApiIconTextItem),
    cooperation: config.cooperation.map(toApiImageTextItem),
    partyBuilding: config.partyBuildingNews.map(toApiImageTextItem),
    productIntros: config.productIntro.map(toApiImageTextItem),
  };

  // 过滤空字段
  Object.keys(payload).forEach(key => {
    if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
};

// ============ 通用组件 ============
function SectionCard({
  title,
  icon: Icon,
  children,
  action
}: {
  title: React.ReactNode;
  icon: any;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#1c5bff]" />
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function DisplayRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right max-w-[60%] truncate">{value || '-'}</span>
    </div>
  );
}

// ============ 图片+标题+富文本 编辑组件 ============
function ImageRichEditor({
  items,
  onChange,
  colorClass = 'border-gray-200'
}: {
  items: ImageTextItem[];
  onChange: (items: ImageTextItem[]) => void;
  colorClass?: string;
}) {
  const add = () => onChange([...items, emptyImageTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const update = (id: string, field: keyof ImageTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div key={item.id} className={`flex gap-3 items-start p-3 rounded-lg border ${colorClass} transition-all relative group`}>
              <button
                onClick={() => remove(item.id)}
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-[50%] shrink-0">
                <ProductImageUploader
                  value={item.image}
                  onChange={(v) => update(item.id, 'image', v as string)}
                  simple
                />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="标题"
                    size="small"
                  />
                </div>
                <ProductRichTextEditor
                  value={item.description}
                  onChange={(v) => update(item.id, 'description', v)}
                  showHeight={false}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ 图标+背景色+标题+富文本 编辑组件 ============
function IconRichEditor({
  items,
  onChange
}: {
  items: IconTextItem[];
  onChange: (items: IconTextItem[]) => void;
}) {
  const add = () => onChange([...items, emptyIconTextItem()]);
  const remove = (id: string) => onChange(items.filter(i => i.id !== id));
  const update = (id: string, field: keyof IconTextItem, value: any) => {
    onChange(items.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="small" theme="primary" variant="outline" onClick={add}>
          <span className="flex items-center gap-1"><Plus className="w-3 h-3" />添加</span>
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50">
          暂无内容，点击"添加"按钮新增
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((item, idx) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 relative group">
              <button
                onClick={() => remove(item.id)}
                className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-red-500 transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
                项目 #{idx + 1}
              </div>
              <div className="space-y-2">
                <IconPicker
                  value={item.icon}
                  onChange={(v) => update(item.id, 'icon', v)}
                  label="图标"
                />
                <ColorPickerField
                  value={item.iconBgColor}
                  onChange={(v) => update(item.id, 'iconBgColor', v)}
                  label="背景色"
                />
                <div>
                  <label className="text-xs text-gray-500 block mb-1">标题</label>
                  <Input
                    value={item.title}
                    onChange={(v) => update(item.id, 'title', v)}
                    placeholder="请输入标题"
                    size="small"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">详细描述（富文本）</label>
                  <ProductRichTextEditor
                    value={item.description}
                    onChange={(v) => update(item.id, 'description', v)}
                    showHeight={false}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ============ 图片+标题 展示组件（不展示富文本） ============
function ImageRichDisplay({ items, emptyText }: { items: ImageTextItem[]; emptyText?: string }) {
  if (!items || items.length === 0) {
    return <div className="text-center py-4 text-gray-400 text-sm">{emptyText || '暂无数据'}</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item, idx) => (
        <div key={item.id} className="rounded-xl overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow group">
          <div className="aspect-[4/3] overflow-hidden">
            {item.image ? (
              <PreviewableImage src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                暂无图片
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold shrink-0">{idx + 1}</span>
              <span className="text-sm font-medium text-gray-800 truncate">{item.title || '未设置标题'}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ 图标+标题 展示组件（不展示富文本） ============
function IconRichDisplay({ items, emptyText }: { items: IconTextItem[]; emptyText?: string }) {
  if (!items || items.length === 0) {
    return <div className="text-center py-4 text-gray-400 text-sm">{emptyText || '暂无数据'}</div>;
  }
  return (
    <div className="grid grid-cols-4 gap-3">
      {items.map((item, idx) => (
        <div key={item.id} className="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow p-4 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-5 h-5 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center font-bold">{idx + 1}</span>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center mb-2"
            style={{ backgroundColor: item.iconBgColor || '#1c5bff' }}
          >
            <DynamicIcon iconName={item.icon} className="w-6 h-6 text-white" />
          </div>
          <span className="text-sm font-medium text-gray-800 truncate w-full">{item.title || '未设置标题'}</span>
        </div>
      ))}
    </div>
  );
}

// ============ 主组件 ============
export default function MicrOfficialWebsite() {
  const [config, setConfig] = useState<ConfigData>(emptyConfig());
  const [isEditing, setIsEditing] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [activeTab, setActiveTab] = useState<string>('');
  const initializedRef = useRef(false);

  const tenantInfo = tokenStorage.getTenantInfo();
  const tenantLevel = tenantInfo?.tenantLevel;
  // 党建动态仅 tenantLevel 6 可见
  const availableSectionOptions = tenantLevel === 6
    ? sectionOptions
    : sectionOptions.filter(o => o.value !== 'partyBuildingNews');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enterpriseSiteApi.get();
      if (res.code === 200 && res.data) {
        setConfig(mapApiToConfig(res.data));
        setHasData(true);
        setIsEditing(false);
      } else {
        setConfig(emptyConfig());
        setHasData(false);
        setIsEditing(true);
      }
    } catch (err) {
      setConfig(emptyConfig());
      setHasData(false);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    loadData();
  }, [loadData]);

  const toggleSection = (sectionValue: string) => {
    setConfig(prev => {
      const sections = prev.selectedSections || [];
      if (sections.includes(sectionValue)) {
        const newSections = sections.filter(s => s !== sectionValue);
        if (activeTab === sectionValue && newSections.length > 0) {
          setActiveTab(newSections[0]);
        } else if (newSections.length === 0) {
          setActiveTab('');
        }
        return { ...prev, selectedSections: newSections };
      } else {
        const newSections = [...sections, sectionValue];
        if (sections.length === 0) {
          setActiveTab(sectionValue);
        }
        return { ...prev, selectedSections: newSections };
      }
    });
  };

  const handleTabDragSort = (context: { current: string; target: string; currentIndex: number; targetIndex: number }) => {
    setConfig(prev => {
      const sections = [...(prev.selectedSections || [])];
      const { currentIndex, targetIndex } = context;
      if (currentIndex === targetIndex) return prev;
      const [movedItem] = sections.splice(currentIndex, 1);
      sections.splice(targetIndex, 0, movedItem);
      return { ...prev, selectedSections: sections };
    });
  };

  const handleTabRemove = (options: { value: string; index: number }) => {
    setConfig(prev => {
      const sections = [...(prev.selectedSections || [])];
      sections.splice(options.index, 1);
      return { ...prev, selectedSections: sections };
    });
    if (activeTab === options.value) {
      setActiveTab('');
    }
  };

  const handleSave = async () => {
    // 1. 企业基本信息必填校验
    if (!config.companyName.trim()) {
      MessagePlugin.warning('请输入企业名称');
      return;
    }
    if (config.companyImages.length === 0) {
      MessagePlugin.warning('请上传企业图片');
      return;
    }
    if (!config.companyDesc.trim()) {
      MessagePlugin.warning('请输入企业介绍');
      return;
    }
    if (!isValidUrl(config.companyUrl)) {
      setUrlError('请输入正确的网址格式，如 https://www.example.com');
      return;
    }
    if (config.companyUrl) {
      setUrlError('');
    }

    // 2. 已勾选的自定义板块内容完整性校验
    const sections = config.selectedSections || [];
    const sectionLabelMap: Record<string, string> = {
      cultureShow: '文化展示', honors: '荣誉资质', leadershipCare: '领导关怀',
      qualifications: '经营资质', investmentProjects: '投资项目', companyStrength: '企业实力',
      cooperation: '合作情况', partyBuildingNews: '党建动态', productIntro: '产品介绍',
      promoVideo: '宣传视频',
    };

    for (const section of sections) {
      if (section === 'promoVideo') {
        if (!config.promoVideo) {
          MessagePlugin.warning(`请上传「${sectionLabelMap[section]}」的视频`);
          return;
        }
        continue;
      }

      // 图文类板块
      const imageTextFields: Record<string, ImageTextItem[]> = {
        cultureShow: config.cultureShow,
        honors: config.honors,
        leadershipCare: config.leadershipCare,
        qualifications: config.qualifications,
        cooperation: config.cooperation,
        partyBuildingNews: config.partyBuildingNews,
        productIntro: config.productIntro,
      };

      if (imageTextFields[section]) {
        const items = imageTextFields[section];
        if (items.length === 0) {
          MessagePlugin.warning(`「${sectionLabelMap[section]}」至少添加一条内容`);
          return;
        }
        for (let i = 0; i < items.length; i++) {
          if (!items[i].image) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请上传图片`);
            return;
          }
          if (!items[i].title.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入标题`);
            return;
          }
          if (!items[i].description.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入描述`);
            return;
          }
        }
      }

      // 图标类板块
      const iconTextFields: Record<string, IconTextItem[]> = {
        investmentProjects: config.investmentProjects,
        companyStrength: config.companyStrength,
      };

      if (iconTextFields[section]) {
        const items = iconTextFields[section];
        if (items.length === 0) {
          MessagePlugin.warning(`「${sectionLabelMap[section]}」至少添加一条内容`);
          return;
        }
        for (let i = 0; i < items.length; i++) {
          if (!items[i].icon) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请选择图标`);
            return;
          }
          if (!items[i].title.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入标题`);
            return;
          }
          if (!items[i].description.trim()) {
            MessagePlugin.warning(`「${sectionLabelMap[section]}」第${i + 1}条请输入描述`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      const apiData = mapConfigToApi(config);
      const res = await enterpriseSiteApi.update(apiData);
      if (res.code === 200) {
        MessagePlugin.success(config.id ? '更新成功' : '保存成功');
        setIsEditing(false);
        setHasData(true);
        if (res.data) {
          setConfig(mapApiToConfig(res.data));
        } else {
          await loadData();
        }
      } else {
        MessagePlugin.error(res.message || '保存失败');
      }
    } catch (err: any) {
      MessagePlugin.error(err?.message || '保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasData) {
      setIsEditing(false);
      loadData();
    } else {
      loadData();
    }
    setUrlError('');
  };

  // ============ Tabs 内容渲染 ============
  const renderTabPanelContent = (sectionValue: string) => {
    switch (sectionValue) {
      case 'cultureShow':
        return (
          <ImageRichEditor
            items={config.cultureShow}
            onChange={(v) => setConfig({ ...config, cultureShow: v })}
            colorClass="border-purple-200 bg-purple-50/30"
          />
        );
      case 'honors':
        return (
          <ImageRichEditor
            items={config.honors}
            onChange={(v) => setConfig({ ...config, honors: v })}
            colorClass="border-amber-200 bg-amber-50/30"
          />
        );
      case 'leadershipCare':
        return (
          <ImageRichEditor
            items={config.leadershipCare}
            onChange={(v) => setConfig({ ...config, leadershipCare: v })}
            colorClass="border-green-200 bg-green-50/30"
          />
        );
      case 'qualifications':
        return (
          <ImageRichEditor
            items={config.qualifications}
            onChange={(v) => setConfig({ ...config, qualifications: v })}
            colorClass="border-blue-200 bg-blue-50/30"
          />
        );
      case 'investmentProjects':
        return (
          <IconRichEditor
            items={config.investmentProjects}
            onChange={(v) => setConfig({ ...config, investmentProjects: v })}
          />
        );
      case 'companyStrength':
        return (
          <IconRichEditor
            items={config.companyStrength}
            onChange={(v) => setConfig({ ...config, companyStrength: v })}
          />
        );
      case 'cooperation':
        return (
          <ImageRichEditor
            items={config.cooperation}
            onChange={(v) => setConfig({ ...config, cooperation: v })}
            colorClass="border-pink-200 bg-pink-50/30"
          />
        );
      case 'partyBuildingNews':
        return (
          <ImageRichEditor
            items={config.partyBuildingNews}
            onChange={(v) => setConfig({ ...config, partyBuildingNews: v })}
            colorClass="border-red-200 bg-red-50/30"
          />
        );
      case 'productIntro':
        return (
          <ImageRichEditor
            items={config.productIntro}
            onChange={(v) => setConfig({ ...config, productIntro: v })}
            colorClass="border-indigo-200 bg-indigo-50/30"
          />
        );
      case 'promoVideo':
        return (
          <div>
            <div className="text-xs text-gray-500 mb-3">上传一个宣传视频即可</div>
            <ProductImageUploader
              value={config.promoVideo}
              onChange={(v) => setConfig({ ...config, promoVideo: typeof v === 'string' ? v : '' })}
              uploadType={2}
              simple
            />
          </div>
        );
      default:
        return null;
    }
  };

  // ============ 编辑模式渲染 ============
  const renderEditMode = () => (
    <div className="space-y-4">
      {/* 企业基本信息 */}
      <SectionCard title="企业基本信息" icon={Building2}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">企业名称<span className="text-red-500 ml-1">*</span></label>
            <Input
              value={config.companyName}
              onChange={(v) => setConfig({ ...config, companyName: v })}
              placeholder="请输入企业名称"
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">企业网址</label>
            <Input
              value={config.companyUrl}
              onChange={(v) => {
                setConfig({ ...config, companyUrl: v });
                if (v && !isValidUrl(v)) {
                  setUrlError('请输入正确的网址格式，如 https://www.example.com');
                } else {
                  setUrlError('');
                }
              }}
              placeholder="请输入企业网址，如 https://www.example.com"
            />
            {urlError && <div className="text-xs text-red-500 mt-1">{urlError}</div>}
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">企业图片（可多张）<span className="text-red-500 ml-1">*</span></label>
            <ProductImageUploader
              value={config.companyImages.length > 0 ? config.companyImages.join(',') : ''}
              onChange={(v) => setConfig({
                ...config,
                companyImages: typeof v === 'string' ? v.split(',').filter(Boolean) : (v as string[])
              })}
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm text-gray-500 block mb-2">企业介绍<span className="text-red-500 ml-1">*</span></label>
            <ProductRichTextEditor
              value={config.companyDesc}
              onChange={(v) => setConfig({ ...config, companyDesc: v })}
            />
          </div>
        </div>
      </SectionCard>

      {/* 企业信息自定义选择配置 */}
      <SectionCard title="企业信息自定义选择配置" icon={Sparkles}>
        <p className="text-xs text-gray-500 mb-4">企业信息自定义选择配置展示</p>

        {/* 选择区域 */}
        <div className="mb-4">
          <div className="text-xs text-gray-600 mb-2 font-medium">可选内容</div>
          <div className="grid grid-cols-5 gap-3">
            {availableSectionOptions.map(option => {
              const isSelected = config.selectedSections?.includes(option.value);
              const IconComp = option.icon;
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-[#1c5bff] bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <Checkbox checked={isSelected} onChange={() => toggleSection(option.value)} />
                  <IconComp className={`w-4 h-4 ${isSelected ? 'text-[#1c5bff]' : 'text-gray-400'}`} />
                  <span className={`text-sm ${isSelected ? 'text-[#1c5bff] font-medium' : 'text-gray-700'}`}>
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Tabs 排列区域 */}
        {config.selectedSections && config.selectedSections.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-600 font-medium">
                已选内容 ({config.selectedSections.length} 项)
              </div>
              <span className="text-[10px] text-gray-400">支持拖拽排序、点击删除</span>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <Tabs
                value={activeTab || config.selectedSections[0]}
                onChange={(val) => setActiveTab(val as string)}
                dragSort
                scrollPosition="auto"
                theme="card"
                size="medium"
                onDragSort={handleTabDragSort}
                onRemove={handleTabRemove}
                className="custom-tabs"
              >
                {config.selectedSections.map((sectionValue) => {
                  const option = sectionOptions.find(o => o.value === sectionValue);
                  if (!option) return null;
                  const IconComp = option.icon;

                  const tabLabel = (
                    <span className="inline-flex items-center gap-1.5 py-1">
                      <IconComp className="w-3.5 h-3.5 text-[#1c5bff]" />
                      <span className="text-xs font-medium">{option.label}</span>
                    </span>
                  );

                  return (
                    <Tabs.TabPanel
                      key={sectionValue}
                      value={sectionValue}
                      label={tabLabel}
                      removable
                    >
                      <div className="p-3 bg-gray-50 rounded-lg max-h-[520px] overflow-y-auto">
                        {renderTabPanelContent(sectionValue)}
                      </div>
                    </Tabs.TabPanel>
                  );
                })}
              </Tabs>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-400 text-xs border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
            暂未选择任何内容，请在上方选择需要展示的页面
          </div>
        )}
      </SectionCard>
    </div>
  );

  // ============ 展示模式渲染 ============
  const renderDisplayMode = () => (
    <div className="flex  gap-4">
      {/* 企业基本信息 */}
      <SectionCard title="企业基本信息" icon={Building2}>
        <DisplayRow label="企业名称" value={config.companyName} />
        <DisplayRow
          label="企业网址"
          value={config.companyUrl ? (
            <a href={config.companyUrl} target="_blank" rel="noopener noreferrer" className="text-[#1c5bff] hover:underline flex items-center gap-1 justify-end">
              <ExternalLink className="w-3 h-3" />
              <span className="truncate max-w-[200px]">{config.companyUrl}</span>
            </a>
          ) : '-'}
        />
        <div className="py-2 border-b border-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">企业图片</span>
            <span className="text-sm text-gray-800 font-medium">
              {config.companyImages.length > 0 ? (
                <div className="flex items-center gap-1">
                  {config.companyImages.slice(0, 4).map((img, idx) => (
                    <PreviewableImage key={idx} src={img} alt={`企业图片${idx + 1}`} className="w-12 h-12 object-cover rounded border" />
                  ))}
                  {config.companyImages.length > 4 && (
                    <span className="text-xs text-gray-400 ml-1">+{config.companyImages.length - 4}</span>
                  )}
                </div>
              ) : '-'}
            </span>
          </div>
        </div>
        <div className="py-2 last:border-0">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">企业介绍</span>
            <span className="text-sm text-gray-800 font-medium text-right max-w-[70%]">
              {config.companyDesc ? (
                <div
                  className="line-clamp-3 text-left"
                  dangerouslySetInnerHTML={{ __html: config.companyDesc }}
                />
              ) : '-'}
            </span>
          </div>
        </div>
        {config.updatedAt && (
          <DisplayRow label="更新时间" value={config.updatedAt} />
        )}
      </SectionCard>

      {/* 自定义选择配置展示 */}
      {config.selectedSections && config.selectedSections.length > 0 && (
        <SectionCard title="自定义选择配置" icon={Sparkles}>
          <div className="flex flex-wrap gap-2 mb-4">
            {config.selectedSections.map((sectionValue, idx) => {
              const option = sectionOptions.find(o => o.value === sectionValue);
              if (!option) return null;
              const IconComp = option.icon;
              return (
                <span key={sectionValue} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-[#1c5bff] border border-[#1c5bff]/30 text-xs">
                  <span className="w-4 h-4 rounded-full bg-[#1c5bff] text-white text-[10px] flex items-center justify-center">{idx + 1}</span>
                  <IconComp className="w-3 h-3" />
                  {option.label}
                </span>
              );
            })}
          </div>

          {/* {config.selectedSections.includes('cultureShow') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-purple-500" /> 文化展示
              </div>
              <ImageRichDisplay items={config.cultureShow} emptyText="暂无文化展示内容" />
            </div>
          )}
          {config.selectedSections.includes('honors') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Award className="w-4 h-4 text-amber-500" /> 荣誉资质
              </div>
              <ImageRichDisplay items={config.honors} emptyText="暂无荣誉资质内容" />
            </div>
          )}
          {config.selectedSections.includes('leadershipCare') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Users className="w-4 h-4 text-green-500" /> 领导关怀
              </div>
              <ImageRichDisplay items={config.leadershipCare} emptyText="暂无领导关怀内容" />
            </div>
          )}
          {config.selectedSections.includes('qualifications') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <FileCheck className="w-4 h-4 text-blue-500" /> 经营资质
              </div>
              <ImageRichDisplay items={config.qualifications} emptyText="暂无经营资质内容" />
            </div>
          )}
          {config.selectedSections.includes('investmentProjects') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Target className="w-4 h-4 text-indigo-500" /> 投资项目
              </div>
              <IconRichDisplay items={config.investmentProjects} emptyText="暂无投资项目内容" />
            </div>
          )}
          {config.selectedSections.includes('companyStrength') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Building2 className="w-4 h-4 text-teal-500" /> 企业实力
              </div>
              <IconRichDisplay items={config.companyStrength} emptyText="暂无企业实力内容" />
            </div>
          )}
          {config.selectedSections.includes('cooperation') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <HeartHandshake className="w-4 h-4 text-pink-500" /> 合作情况
              </div>
              <ImageRichDisplay items={config.cooperation} emptyText="暂无合作情况内容" />
            </div>
          )}
          {config.selectedSections.includes('partyBuildingNews') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Flag className="w-4 h-4 text-red-500" /> 党建动态
              </div>
              <ImageRichDisplay items={config.partyBuildingNews} emptyText="暂无党建动态内容" />
            </div>
          )}
          {config.selectedSections.includes('productIntro') && (
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Package className="w-4 h-4 text-indigo-500" /> 产品介绍
              </div>
              <ImageRichDisplay items={config.productIntro} emptyText="暂无产品介绍内容" />
            </div>
          )}
          {config.selectedSections.includes('promoVideo') && (
            <div className="mb-2">
              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                <Video className="w-4 h-4 text-cyan-500" /> 宣传视频
              </div>
              {config.promoVideo ? (
                <video
                  src={config.promoVideo}
                  controls
                  className="w-full max-w-md rounded-lg border border-gray-200"
                />
              ) : <span className="text-gray-400 text-sm">-</span>}
            </div>
          )} */}
        </SectionCard>
      )}
    </div>
  );

  const showEditActions = isEditing;
  const showReadOnlyActions = !isEditing && hasData;

  return (
    <ImagePreviewProvider>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#1c5bff]" />
            微信官方网站配置
          </h1>
          {showReadOnlyActions && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] transition-colors flex items-center gap-2 text-sm"
            >
              <Edit3 className="w-4 h-4" />
              编辑
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1c5bff] mr-3"></div>
            加载中...
          </div>
        ) : (
          <div className="space-y-4">
            {isEditing ? renderEditMode() : renderDisplayMode()}

            {showEditActions && (
              <div className="sticky bottom-0 bg-white border-t border-gray-100 rounded-xl shadow-sm p-4 flex items-center justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#1c5bff] text-white rounded-lg hover:bg-[#1547cc] disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {config.id ? '更新' : '保存'}
                </button>
              </div>
            )}
          </div>
        )}

        <style>{`
          .custom-tabs .t-tabs__nav-item {
            padding: 8px 14px;
            border-radius: 8px 8px 0 0;
            transition: all 0.2s ease;
            gap: 4px;
            cursor: grab;
          }
          .custom-tabs .t-tabs__nav-item:active {
            cursor: grabbing;
          }
          .custom-tabs .t-tabs__nav-item:hover {
            background-color: #f5f5f5;
          }
          .custom-tabs .t-tabs__nav-item.t-is-active {
            font-weight: 600;
            color: #1c5bff;
            border-bottom: 2px solid #1c5bff;
          }
          .custom-tabs .t-tabs__nav-item.t-is-dragging {
            opacity: 0.5;
            background-color: #e3f2fd;
            box-shadow: 0 4px 12px rgba(28, 91, 255, 0.2);
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close {
            margin-left: 4px;
            font-size: 12px;
          }
          .custom-tabs .t-tabs__nav-item .t-icon-close:hover {
            color: #ff4d4f;
          }
          .custom-tabs .t-tabs__content {
            padding-top: 12px;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item {
            border: 1px solid #e5e7eb;
            border-bottom: none;
            background: #fafafa;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-active {
            background: #ffffff;
            border-color: #1c5bff;
            color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card .t-tabs__nav-item.t-is-dragging {
            background: #e3f2fd;
            border-color: #1c5bff;
          }
          .custom-tabs .t-tabs__nav--card + .t-tabs__content {
            border: 1px solid #e5e7eb;
            border-top: none;
            border-radius: 0 0 8px 8px;
            background: #ffffff;
          }
        `}</style>
      </div>
    </ImagePreviewProvider>
  );
}